// packages/adapter/src/tonweb/index.ts
import TonWeb from "tonweb";

// packages/adapter/src/common/index.ts
function isPostMethod(method) {
  const postMethods = /* @__PURE__ */ new Set(["runGetMethod", "sendBoc", "sendBocReturnHash", "sendQuery"]);
  return postMethods.has(method);
}
function appendSearchParam(url, params) {
  const urlObj = new URL(url);
  for (const [k, v] of Object.entries(params)) {
    if (v == null)
      continue;
    urlObj.searchParams.append(k, v);
  }
  return urlObj.toString();
}

// packages/adapter/src/tonweb/index.ts
var TonWebWrappedHttpProvider = class extends TonWeb.HttpProvider {
  constructor(props, apiKey) {
    super(props);
    this.sendImpl = async (apiUrl, request) => {
      const method = request.method;
      const endpoint = apiUrl.replace("@@METHOD@@", method);
      const shouldSendPost = isPostMethod(method);
      const headers = {
        "Content-Type": "application/json",
        "API_KEY": this.apiKey
      };
      let call;
      if (shouldSendPost) {
        call = fetch(endpoint, {
          method: "POST",
          headers,
          body: JSON.stringify(request.params)
        });
      } else {
        const url = appendSearchParam(endpoint, request.params);
        call = fetch(url, {
          method: "GET",
          headers
        });
      }
      return call.then((response) => response.json()).then(({ result, error }) => result || Promise.reject(error));
    };
    this.apiKey = apiKey;
  }
};
var TonWebAdapter = class extends TonWeb {
  constructor({
    network,
    apiKey
  }) {
    super(
      //@ts-ignore
      new TonWebWrappedHttpProvider(
        `https://${network}-rpc.tonxapi.com/migration/ton-center/@@METHOD@@`,
        apiKey
      )
    );
  }
};
var tonweb_default = TonWebAdapter;

// packages/adapter/src/toncore/index.ts
import axios from "axios";
import { TonClient, HttpApi } from "@ton/ton";
async function httpApiDoCall(method, body, codec) {
  const parameters = this.parameters;
  const apiKey = parameters.apiKey;
  const headers = {
    "Content-Type": "application/json",
    "API_KEY": apiKey
  };
  const axiosInstance = axios.create({
    headers,
    timeout: parameters.timeout || 1e4,
    transformResponse: [(data) => {
      try {
        const parsedData = JSON.parse(data);
        return {
          ...parsedData,
          ok: true
        };
      } catch (error) {
        return { ok: false, error: "Failed to parse response" };
      }
    }]
  });
  const endpoint = this.endpoint.replace("@@METHOD@@", method);
  const shouldSendPost = isPostMethod(method);
  try {
    const response = shouldSendPost ? await axiosInstance.post(endpoint, JSON.stringify(body)) : await axiosInstance.get(appendSearchParam(endpoint, body));
    if (response.status !== 200 || !response.data.ok) {
      const error = new Error("Received error: " + JSON.stringify(response.data));
      error.response = {
        data: { ok: false, error: response.data.error || "Unknown error" }
      };
      throw error;
    }
    const decoded = codec.safeParse(response.data.result);
    if (!decoded.success) {
      const error = new Error("Malformed response: " + decoded.error.format()._errors.join(", "));
      error.response = {
        data: { ok: false, error: "Invalid response format" }
      };
      throw error;
    }
    return decoded.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      const errorData = {
        ok: false,
        error: error.response?.data?.error || error.message
      };
      const newError2 = new Error(JSON.stringify(errorData));
      newError2.response = { data: errorData };
      throw newError2;
    }
    if (error instanceof Error && error.response) {
      throw error;
    }
    const newError = new Error(JSON.stringify({
      ok: false,
      error: error instanceof Error ? error.message : "Unknown error"
    }));
    newError.response = {
      data: { ok: false, error: error instanceof Error ? error.message : "Unknown error" }
    };
    throw newError;
  }
}
var ProxiedHttp = new Proxy(HttpApi, {
  construct(target, args) {
    const instance = Reflect.construct(target, args);
    instance.doCall = httpApiDoCall.bind(instance);
    return instance;
  }
});
var ToncoreAdapter = class extends TonClient {
  constructor({
    apiKey,
    network
  }) {
    super({
      endpoint: ""
    });
    const endpoint = `https://${network}-rpc.tonxapi.com/migration/ton-center/@@METHOD@@`;
    this.api = new ProxiedHttp(endpoint, {
      timeout: 1e4,
      apiKey
    });
  }
};
var toncore_default = ToncoreAdapter;

// packages/adapter/src/tonclient4/index.ts
import { openContract as openContract2, Cell as Cell2, loadTransaction, serializeTuple, parseTuple, TupleReader as TupleReader3 } from "@ton/core";

// packages/adapter/package.json
var version = "2.0.7";

// packages/adapter/src/tonclient4/utils.ts
function getDomain(network) {
  return `https://${network}-rpc.tonxapi.com`;
}
function getJsonRpcUrl(network, apiKey) {
  return `${getDomain(network)}/v2/json-rpc/${apiKey}`;
}
function getRestUrl(path, network, apiKey) {
  return `${getDomain(network)}/migration/ton-center${path}`;
}
function convertHexShardToSignedNumberStr(hexShard) {
  const num = BigInt(`0x${hexShard}`);
  const signedNum = num >= 2n ** 63n ? num - 2n ** 64n : num;
  return signedNum.toString();
}
function toUrlSafe(src) {
  while (src.indexOf("/") >= 0) {
    src = src.replace("/", "_");
  }
  while (src.indexOf("+") >= 0) {
    src = src.replace("+", "-");
  }
  while (src.indexOf("=") >= 0) {
    src = src.replace("=", "");
  }
  return src;
}
function getTonhubDomain(network) {
  if (network === "mainnet" || network === "testnet") {
    return `https://${network}-rpc.tonxapi.com/migration/ton-http-api`;
  } else {
    throw new Error("Invalid network");
  }
}

// packages/adapter/src/tonclient4/types/lastBlock.ts
import { z } from "zod";
var lastBlockCodec = z.object({
  last: z.object({
    seqno: z.number(),
    shard: z.string(),
    workchain: z.number(),
    fileHash: z.string(),
    rootHash: z.string()
  }),
  init: z.object({
    fileHash: z.string(),
    rootHash: z.string()
  }),
  stateRootHash: z.string(),
  now: z.number()
});

// packages/adapter/src/tonclient4/types/block.ts
import { z as z2 } from "zod";
var blockCodec = z2.union([z2.object({
  exist: z2.literal(false)
}), z2.object({
  exist: z2.literal(true),
  block: z2.object({
    shards: z2.array(z2.object({
      workchain: z2.number(),
      seqno: z2.number(),
      shard: z2.string(),
      rootHash: z2.string(),
      fileHash: z2.string(),
      transactions: z2.array(z2.object({
        account: z2.string(),
        hash: z2.string(),
        lt: z2.string()
      }))
    }))
  })
})]);

// packages/adapter/src/tonclient4/types/account.ts
import { z as z3 } from "zod";
var storageStatCodec = z3.object({
  lastPaid: z3.number(),
  duePayment: z3.union([z3.null(), z3.string()]),
  used: z3.object({
    bits: z3.number(),
    cells: z3.number(),
    publicCells: z3.number()
  })
});
var accountCodec = z3.object({
  account: z3.object({
    state: z3.union([
      z3.object({ type: z3.literal("uninit") }),
      z3.object({ type: z3.literal("active"), code: z3.union([z3.string(), z3.null()]), data: z3.union([z3.string(), z3.null()]) }),
      z3.object({ type: z3.literal("frozen"), stateHash: z3.string() })
    ]),
    balance: z3.object({
      coins: z3.string()
    }),
    last: z3.union([
      z3.null(),
      z3.object({
        lt: z3.string(),
        hash: z3.string()
      })
    ]),
    storageStat: z3.union([z3.null(), storageStatCodec])
  }),
  block: z3.object({
    workchain: z3.number(),
    seqno: z3.number(),
    shard: z3.string(),
    rootHash: z3.string(),
    fileHash: z3.string()
  })
});
var accountLiteCodec = z3.object({
  account: z3.object({
    state: z3.union([
      z3.object({ type: z3.literal("uninit") }),
      z3.object({ type: z3.literal("active"), codeHash: z3.string(), dataHash: z3.string() }),
      z3.object({ type: z3.literal("frozen"), stateHash: z3.string() })
    ]),
    balance: z3.object({
      coins: z3.string()
    }),
    last: z3.union([
      z3.null(),
      z3.object({
        lt: z3.string(),
        hash: z3.string()
      })
    ]),
    storageStat: z3.union([z3.null(), storageStatCodec])
  })
});

// packages/adapter/src/tonclient4/types/accountTransactions.ts
import { z as z4 } from "zod";
import { Address } from "@ton/core";
var accountStatusCodec = z4.enum(["uninitialized", "frozen", "active", "non-existing"]);
var commonMessageInfoCodec = z4.union([
  z4.object({
    type: z4.literal("internal"),
    ihrDisabled: z4.boolean(),
    bounce: z4.boolean(),
    bounced: z4.boolean(),
    src: z4.instanceof(Address),
    dest: z4.instanceof(Address),
    value: z4.object({
      coins: z4.bigint(),
      other: z4.map(z4.number(), z4.bigint()).optional()
    }),
    ihrFee: z4.bigint(),
    forwardFee: z4.bigint(),
    createdLt: z4.bigint(),
    createdAt: z4.number()
  }),
  z4.object({
    type: z4.literal("external-in"),
    src: z4.instanceof(Address).optional(),
    dest: z4.instanceof(Address),
    importFee: z4.bigint()
  }),
  z4.object({
    type: z4.literal("external-out"),
    src: z4.instanceof(Address),
    dest: z4.instanceof(Address).optional(),
    createdLt: z4.bigint(),
    createdAt: z4.number()
  })
]);
var blocksCodec = z4.array(z4.object({
  workchain: z4.number(),
  seqno: z4.number(),
  shard: z4.string(),
  rootHash: z4.string(),
  fileHash: z4.string()
}));
var transactionsCodec = z4.object({
  blocks: blocksCodec,
  boc: z4.string()
});

// packages/adapter/src/tonclient4/types/parsedTransactions.ts
import { z as z5 } from "zod";
var parsedOperationItemCodec = z5.union([
  z5.object({ kind: z5.literal("ton"), amount: z5.string() }),
  z5.object({ kind: z5.literal("token"), amount: z5.string() })
]);
var supportedMessageTypeCodec = z5.union([
  z5.literal("jetton::excesses"),
  z5.literal("jetton::transfer"),
  z5.literal("jetton::transfer_notification"),
  z5.literal("deposit"),
  z5.literal("deposit::ok"),
  z5.literal("withdraw"),
  z5.literal("withdraw::all"),
  z5.literal("withdraw::delayed"),
  z5.literal("withdraw::ok"),
  z5.literal("airdrop")
]);
var txBodyCodec = z5.union([
  z5.object({ type: z5.literal("comment"), comment: z5.string() }),
  z5.object({ type: z5.literal("payload"), cell: z5.string() })
]);
var parsedAddressExternalCodec = z5.object({
  bits: z5.number(),
  data: z5.string()
});
var opCodec = z5.object({
  type: supportedMessageTypeCodec,
  options: z5.optional(z5.record(z5.string()))
});
var parsedOperationCodec = z5.object({
  address: z5.string(),
  comment: z5.optional(z5.string()),
  items: z5.array(parsedOperationItemCodec),
  op: z5.optional(opCodec)
});
var parsedMessageInfoCodec = z5.union([
  z5.object({
    type: z5.literal("internal"),
    value: z5.string(),
    dest: z5.string(),
    src: z5.string(),
    bounced: z5.boolean(),
    bounce: z5.boolean(),
    ihrDisabled: z5.boolean(),
    createdAt: z5.number(),
    createdLt: z5.string(),
    fwdFee: z5.string(),
    ihrFee: z5.string()
  }),
  z5.object({
    type: z5.literal("external-in"),
    dest: z5.string(),
    src: z5.union([parsedAddressExternalCodec, z5.null()]),
    importFee: z5.string()
  }),
  z5.object({
    type: z5.literal("external-out"),
    dest: z5.union([parsedAddressExternalCodec, z5.null()])
  })
]);
var parsedStateInitCodec = z5.object({
  splitDepth: z5.union([z5.number(), z5.null()]),
  code: z5.union([z5.string(), z5.null()]),
  data: z5.union([z5.string(), z5.null()]),
  special: z5.union([z5.object({ tick: z5.boolean(), tock: z5.boolean() }), z5.null()])
});
var parsedMessageCodec = z5.object({
  body: z5.string(),
  info: parsedMessageInfoCodec,
  init: z5.union([parsedStateInitCodec, z5.null()])
});
var parsedTransactionCodec = z5.object({
  address: z5.string(),
  lt: z5.string(),
  hash: z5.string(),
  prevTransaction: z5.object({
    lt: z5.string(),
    hash: z5.string()
  }),
  time: z5.number(),
  outMessagesCount: z5.number(),
  oldStatus: accountStatusCodec,
  newStatus: accountStatusCodec,
  fees: z5.string(),
  update: z5.object({
    oldHash: z5.string(),
    newHash: z5.string()
  }),
  inMessage: z5.union([parsedMessageCodec, z5.null()]),
  outMessages: z5.array(parsedMessageCodec),
  parsed: z5.object({
    seqno: z5.union([z5.number(), z5.null()]),
    body: z5.union([txBodyCodec, z5.null()]),
    status: z5.union([z5.literal("success"), z5.literal("failed"), z5.literal("pending")]),
    dest: z5.union([z5.string(), z5.null()]),
    kind: z5.union([z5.literal("out"), z5.literal("in")]),
    amount: z5.string(),
    resolvedAddress: z5.string(),
    bounced: z5.boolean(),
    mentioned: z5.array(z5.string())
  }),
  operation: parsedOperationCodec
});
var parsedTransactionsCodec = z5.object({
  blocks: blocksCodec,
  transactions: z5.array(parsedTransactionCodec)
});

// packages/adapter/src/tonclient4/types/runMethod.ts
import { z as z6 } from "zod";
import { TupleReader } from "@ton/core";
var runMethodCodec = z6.object({
  exitCode: z6.number(),
  resultRaw: z6.union([z6.string(), z6.null()]),
  reader: z6.instanceof(TupleReader).optional(),
  block: z6.object({
    workchain: z6.number(),
    seqno: z6.number(),
    shard: z6.string(),
    rootHash: z6.string(),
    fileHash: z6.string()
  }),
  shardBlock: z6.object({
    workchain: z6.number(),
    seqno: z6.number(),
    shard: z6.string(),
    rootHash: z6.string(),
    fileHash: z6.string()
  })
});

// packages/adapter/src/tonclient4/types/send.ts
import { z as z7 } from "zod";
var sendCodec = z7.object({
  status: z7.number()
});

// packages/adapter/src/tonclient4/types/config.ts
import { z as z8 } from "zod";
var configCodec = z8.object({
  config: z8.object({
    cell: z8.string(),
    address: z8.string(),
    globalBalance: z8.object({
      coins: z8.string()
    })
  })
});

// packages/adapter/src/tonclient4/types/changed.ts
import { z as z9 } from "zod";
var changedCodec = z9.object({
  changed: z9.boolean(),
  block: z9.object({
    workchain: z9.number(),
    seqno: z9.number(),
    shard: z9.string(),
    rootHash: z9.string(),
    fileHash: z9.string()
  })
});

// packages/adapter/src/tonclient4/converters/convertLastBlock.ts
function convertLastBlock(data) {
  return {
    init: {
      fileHash: data.result.first.file_hash,
      rootHash: data.result.first.root_hash
    },
    last: {
      fileHash: data.result.last.file_hash,
      rootHash: data.result.last.root_hash,
      seqno: data.result.last.seqno,
      shard: convertHexShardToSignedNumberStr(data.result.last.shard),
      workchain: data.result.last.workchain
    },
    stateRootHash: "",
    now: Math.floor(Date.now() / 1e3)
  };
}

// packages/adapter/src/tonclient4/converters/convertGetAccountLite.ts
function convertGetAccountLite(data) {
  let result;
  const state = data.account.state;
  if (state.type === "active") {
    result = {
      account: {
        ...data.account,
        state: {
          type: state.type,
          codeHash: state.code || "",
          dataHash: state.data || ""
        }
      }
    };
  } else {
    result = {
      account: {
        ...data.account,
        state
      }
    };
  }
  return result;
}

// packages/adapter/src/tonclient4/converters/convertIsAccountChanged.ts
function convertIsAccountChanged(beforeAccount, afterAccount) {
  return {
    changed: beforeAccount.account.state.type !== afterAccount.account.state.type,
    block: {
      workchain: beforeAccount.block.workchain,
      seqno: beforeAccount.block.seqno,
      shard: beforeAccount.block.shard,
      rootHash: beforeAccount.block.rootHash,
      fileHash: beforeAccount.block.fileHash
    }
  };
}

// packages/adapter/src/tonclient4/converters/convertSend.ts
function convertSendMessage(data) {
  return {
    status: data.result ? 1 : 0
    // to check the status value
  };
}

// packages/adapter/src/tonclient4/createProvider.ts
import { beginCell, comment, external, openContract, storeMessage, toNano, TupleReader as TupleReader2 } from "@ton/core";
function createProvider(client, block, address, init) {
  return {
    async getState() {
      let sq = block;
      if (sq === null) {
        let res = await client.getLastBlock();
        sq = res.last.seqno;
      }
      let state = await client.getAccount(sq, address);
      let last = state.account.last ? { lt: BigInt(state.account.last.lt), hash: Buffer.from(state.account.last.hash, "base64") } : null;
      let storage;
      if (state.account.state.type === "active") {
        storage = {
          type: "active",
          code: state.account.state.code ? Buffer.from(state.account.state.code, "base64") : null,
          data: state.account.state.data ? Buffer.from(state.account.state.data, "base64") : null
        };
      } else if (state.account.state.type === "uninit") {
        storage = {
          type: "uninit"
        };
      } else if (state.account.state.type === "frozen") {
        storage = {
          type: "frozen",
          stateHash: Buffer.from(state.account.state.stateHash, "base64")
        };
      } else {
        throw Error("Unsupported state");
      }
      return {
        balance: BigInt(state.account.balance.coins),
        last,
        state: storage
      };
    },
    async get(name, args) {
      let sq = block;
      if (sq === null) {
        let res = await client.getLastBlock();
        sq = res.last.seqno;
      }
      let method = await client.runMethod(sq, address, name, args);
      if (method.exitCode !== 0 && method.exitCode !== 1) {
        throw Error("Exit code: " + method.exitCode);
      }
      return {
        stack: new TupleReader2(method.result)
      };
    },
    async external(message) {
      let last = await client.getLastBlock();
      let neededInit = null;
      if (init && (await client.getAccount(last.last.seqno, address)).account.state.type !== "active") {
        neededInit = init;
      }
      const ext = external({
        to: address,
        init: neededInit,
        body: message
      });
      let pkg = beginCell().store(storeMessage(ext)).endCell().toBoc();
      await client.sendMessage(pkg);
    },
    async internal(via, message) {
      let last = await client.getLastBlock();
      let neededInit = null;
      if (init && (await client.getAccount(last.last.seqno, address)).account.state.type !== "active") {
        neededInit = init;
      }
      let bounce = true;
      if (message.bounce !== null && message.bounce !== void 0) {
        bounce = message.bounce;
      }
      let value;
      if (typeof message.value === "string") {
        value = toNano(message.value);
      } else {
        value = message.value;
      }
      let body = null;
      if (typeof message.body === "string") {
        body = comment(message.body);
      } else if (message.body) {
        body = message.body;
      }
      await via.send({
        to: address,
        value,
        bounce,
        sendMode: message.sendMode,
        init: neededInit,
        body
      });
    },
    open(contract) {
      return openContract(contract, (args) => createProvider(client, block, args.address, args.init ?? null));
    },
    async getTransactions(address2, lt, hash, limit) {
      const useLimit = typeof limit === "number";
      if (useLimit && limit <= 0) {
        return [];
      }
      let transactions = [];
      do {
        const txs = await client.getAccountTransactions(address2, lt, hash);
        const firstTx = txs[0].tx;
        const [firstLt, firstHash] = [firstTx.lt, firstTx.hash()];
        const needSkipFirst = transactions.length > 0 && firstLt === lt && firstHash.equals(hash);
        if (needSkipFirst) {
          txs.shift();
        }
        if (txs.length === 0) {
          break;
        }
        const lastTx = txs[txs.length - 1].tx;
        const [lastLt, lastHash] = [lastTx.lt, lastTx.hash()];
        if (lastLt === lt && lastHash.equals(hash)) {
          break;
        }
        transactions.push(...txs.map((tx) => tx.tx));
        lt = lastLt;
        hash = lastHash;
      } while (useLimit && transactions.length < limit);
      if (useLimit) {
        transactions = transactions.slice(0, limit);
      }
      return transactions;
    }
  };
}

// packages/adapter/src/tonclient4/index.ts
var TonClient4Adapter = class {
  constructor({
    apiKey,
    network
  }) {
    this.apiKey = apiKey;
    this.network = network;
    this.endpoint = getJsonRpcUrl(network, apiKey);
  }
  version() {
    return version;
  }
  getRestEndpoint(path) {
    return getRestUrl(path, this.network, this.apiKey);
  }
  async sendRpc(method, params) {
    const res = await fetch(this.endpoint, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify({
        id: 0,
        jsonrpc: "2.0",
        method,
        params
      })
    });
    const data = await res.json();
    return data;
  }
  async sendRest(path, method, params) {
    let url = this.getRestEndpoint(path);
    if (method === "GET" && params) {
      url = appendSearchParam(url, params);
    }
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json"
      },
      body: method !== "GET" ? JSON.stringify(params) : void 0
    });
    return response.json();
  }
  async sendToAPIRequest(path, method, params) {
    const endpoint = getTonhubDomain(this.network);
    let url = `${endpoint}${path}`;
    const response = await fetch(url, {
      method,
      headers: {
        "Content-Type": "application/json",
        "API_KEY": this.apiKey
      },
      body: method !== "GET" ? JSON.stringify(params) : void 0
    });
    return response.json();
  }
  /**
     * Get Last Block
     * @returns last block info
     */
  async getLastBlock() {
    const data = await this.sendRpc("getMasterchainInfo");
    const result = convertLastBlock(data);
    let lastBlock = lastBlockCodec.safeParse(result);
    if (!lastBlock.success) {
      throw Error("Mailformed response: " + lastBlock.error.format()._errors.join(", "));
    }
    return lastBlock.data;
  }
  /**
     * Get block info
     * @param seqno block sequence number
     * @returns block info
     */
  async getBlock(seqno) {
    const result = await this.sendToAPIRequest("/block/" + seqno, "GET");
    let block = blockCodec.safeParse(result);
    if (!block.success) {
      throw Error("Mailformed response");
    }
    if (!block.data.exist) {
      throw Error("Block is out of scope");
    }
    return block.data.block;
  }
  /**
     * Get block info by unix timestamp
     * @param ts unix timestamp
     * @returns block info
     */
  async getBlockByUtime(ts) {
    const result = await this.sendToAPIRequest("/block/utime/" + ts, "GET");
    let block = blockCodec.safeParse(result);
    if (!block.success) {
      throw Error("Mailformed response");
    }
    if (!block.data.exist) {
      throw Error("Block is out of scope");
    }
    return block.data.block;
  }
  /**
     * Get block info by seqno
     * @param seqno block sequence number
     * @param address account address
     * @returns account info
     */
  async getAccount(seqno, address) {
    const result = await this.sendToAPIRequest("/block/" + seqno + "/" + address.toString({ urlSafe: true }), "GET");
    let account = accountCodec.safeParse(result);
    if (!account.success) {
      throw Error("Mailformed response");
    }
    return account.data;
  }
  /**
     * Get account lite info (without code and data)
     * @param seqno block sequence number
     * @param address account address
     * @returns account lite info
     */
  async getAccountLite(seqno, address) {
    const data = await this.getAccount(seqno, address);
    const result = convertGetAccountLite(data);
    let account = accountLiteCodec.safeParse(result);
    if (!account.success) {
      throw Error("Mailformed response");
    }
    return account.data;
  }
  /**
     * Check if contract is deployed
     * @param address addres to check
     * @returns true if contract is in active state
     */
  async isContractDeployed(seqno, address) {
    let account = await this.getAccountLite(seqno, address);
    return account.account.state.type === "active";
  }
  /**
     * Check if account was updated since
     * @param seqno block sequence number
     * @param address account address
     * @param lt account last transaction lt
     * @returns account change info
     */
  async isAccountChanged(seqno, address, lt) {
    const lastBlock = await this.getLastBlock();
    const beforeAccount = await this.getAccount(seqno, address);
    const afterAccount = await this.getAccount(lastBlock.last.seqno, address);
    const result = convertIsAccountChanged(beforeAccount, afterAccount);
    const changed = changedCodec.safeParse(result);
    if (!changed.success) {
      throw Error("Mailformed response");
    }
    return changed.data;
  }
  /**
     * Load unparsed account transactions
     * @param address address
     * @param lt last transaction lt
     * @param hash last transaction hash
     * @returns unparsed transactions
     */
  async getAccountTransactions(address, lt, hash) {
    const path = "/account/" + address.toString({ urlSafe: true }) + "/tx/" + lt.toString(10) + "/" + toUrlSafe(hash.toString("base64"));
    const tonhubData = await this.sendToAPIRequest(path, "GET");
    let transactions = transactionsCodec.safeParse(tonhubData);
    if (!transactions.success) {
      throw Error("Mailformed response");
    }
    const data = transactions.data;
    let tx = [];
    let cells = Cell2.fromBoc(Buffer.from(data.boc, "base64"));
    for (let i = 0; i < data.blocks.length; i++) {
      tx.push({
        block: data.blocks[i],
        tx: loadTransaction(cells[i].beginParse())
      });
    }
    return tx;
  }
  /**
     * Load parsed account transactions
     * @param address address
     * @param lt last transaction lt
     * @param hash last transaction hash
     * @param count number of transactions to load
     * @returns parsed transactions
     */
  async getAccountTransactionsParsed(address, lt, hash, count = 20) {
    const path = appendSearchParam("/account/" + address.toString({ urlSafe: true }) + "/tx/parsed/" + lt.toString(10) + "/" + toUrlSafe(hash.toString("base64")), { count: count.toString() });
    const tonhubData = await this.sendToAPIRequest(path, "GET");
    const parsedTransactionsRes = parsedTransactionsCodec.safeParse(tonhubData);
    if (!parsedTransactionsRes.success) {
      throw Error("Mailformed response");
    }
    return parsedTransactionsRes.data;
  }
  /**
     * Get network config
     * @param seqno block sequence number
     * @param ids optional config ids
     * @returns network config
     */
  async getConfig(seqno, ids) {
    let tail = "";
    if (ids && ids.length > 0) {
      tail = "/" + [...ids].sort().join(",");
    }
    const result = await this.sendToAPIRequest("/block/" + seqno + "/config" + tail, "GET");
    const config = configCodec.safeParse(result);
    if (!config.success) {
      throw Error("Mailformed response");
    }
    return config.data;
  }
  /**
     * Execute run method
     * @param seqno block sequence number
     * @param address account address
     * @param name method name
     * @param args method arguments
     * @returns method result
     */
  async runMethod(seqno, address, name, args) {
    const tail = args && args.length > 0 ? "/" + toUrlSafe(serializeTuple(args).toBoc({ idx: false, crc32: false }).toString("base64")) : "";
    const path = "/block/" + seqno + "/" + address.toString({ urlSafe: true }) + "/run/" + encodeURIComponent(name) + tail;
    const res = await this.sendToAPIRequest(path, "GET");
    const runMethod = runMethodCodec.safeParse(res);
    if (!runMethod.success) {
      throw Error("Mailformed response");
    }
    const resultTuple = runMethod.data.resultRaw ? parseTuple(Cell2.fromBoc(Buffer.from(runMethod.data.resultRaw, "base64"))[0]) : [];
    return {
      exitCode: runMethod.data.exitCode,
      result: resultTuple,
      resultRaw: runMethod.data.resultRaw,
      block: runMethod.data.block,
      shardBlock: runMethod.data.shardBlock,
      reader: new TupleReader3(resultTuple)
    };
  }
  /**
     * Send external message
     * @param message message boc
     * @returns message status
     */
  async sendMessage(message) {
    const res = await this.sendRpc("sendMessage", { boc: message.toString("base64") });
    const data = convertSendMessage(res);
    let send = sendCodec.safeParse(data);
    if (!send.success) {
      throw Error("Mailformed response");
    }
    return { status: send.data.status };
  }
  /**
     * Open smart contract
     * @param contract contract
     * @returns opened contract
     */
  open(contract) {
    return openContract2(contract, (args) => createProvider(this, null, args.address, args.init));
  }
  /**
   * Open smart contract
   * @param block block number
   * @param contract contract
   * @returns opened contract
   */
  openAt(block, contract) {
    return openContract2(contract, (args) => createProvider(this, block, args.address, args.init));
  }
  /**
   * Create provider
   * @param address address
   * @param init optional init data
   * @returns provider
   */
  provider(address, init) {
    return createProvider(this, null, address, init ?? null);
  }
  /**
   * Create provider at specified block number
   * @param block block number
   * @param address address
   * @param init optional init data
   * @returns provider
   */
  providerAt(block, address, init) {
    return createProvider(this, block, address, init ?? null);
  }
};
var tonclient4_default = TonClient4Adapter;
export {
  tonclient4_default as TonClient4Adapter,
  tonweb_default as TonWebAdapter,
  toncore_default as ToncoreAdapter
};
